import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-bootstrap/Modal";
import FormInputs from "../UI/FormInputs";
import Buttons from "../UI/Buttons";
import UserService from "../../services/UserService";
import { isSet } from "../../utils/commonUtils";

const KIOSK_PIN_INPUT_PROPS = {
  maxLength: 4,
  "data-lpignore": "true",
  "data-1p-ignore": "true",
  "data-bwignore": "true",
};

/**
 * Kiosk idle lock — staff kiosk PIN required to continue (not staff login).
 * Uses the same Bootstrap modal pattern as other app dialogs so the form stays visible behind the backdrop.
 */
function KioskIdleLockOverlay({ isOpen, onUnlock, Toast }) {
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validation, setValidation] = useState({});

  useEffect(() => {
    if (isOpen) {
      setPin("");
      setValidation({});
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    const v = {};
    const digits = String(pin || "").replace(/\D/g, "").slice(0, 4);
    if (digits.length !== 4) v.pin = "PIN must be 4 digits";
    setValidation(v);
    if (Object.keys(v).length) return;

    setSubmitting(true);
    try {
      await UserService.verifyKioskPin(digits);
      setPin("");
      setValidation({});
      onUnlock?.();
    } catch (e) {
      const msg =
        e?.data?.message ||
        e?.message ||
        (typeof e?.data === "string" ? e.data : null) ||
        "Invalid kiosk PIN.";
      Toast?.error?.(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={Boolean(isOpen)}
      onHide={() => {}}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header>
        <Modal.Title>
          <h6 className="text-lg mb-0 font-weight-bold">Session expired</h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Enter your kiosk PIN to continue the patient forms.
        </p>
        <form
          autoComplete="off"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="row">
            <div className="col-12">
              <label className="mb-0 text-sm text-dark" htmlFor="snap-kiosk-idle-pin">
                Kiosk PIN
              </label>
              <FormInputs
                type="Password"
                id="snap-kiosk-idle-pin"
                className="form-control form-control-solid"
                placeholder="Enter PIN (4 digits)"
                name="snap_kiosk_idle_pin"
                autocomplete="off"
                value={pin}
                inputProps={KIOSK_PIN_INPUT_PROPS}
                changeHandler={(val) => {
                  const digitsOnly = String(val ?? "").replace(/\D/g, "").slice(0, 4);
                  setPin(digitsOnly);
                  setValidation((prev) => ({ ...prev, pin: "" }));
                }}
              />
              {isSet(validation.pin, "") !== "" ? (
                <span className="text-danger m-1">{validation.pin}</span>
              ) : null}
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-center">
        <Buttons
          type="Single"
          className="btn btn-primary"
          label={submitting ? "Verifying…" : "Continue"}
          clickHandler={handleSubmit}
          acl="any"
        />
      </Modal.Footer>
    </Modal>
  );
}

KioskIdleLockOverlay.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onUnlock: PropTypes.func.isRequired,
  Toast: PropTypes.shape({ error: PropTypes.func, success: PropTypes.func }),
};

export default KioskIdleLockOverlay;
